/*
 * ARCHER — FAQ quick-access drawer (Ask ARCHER concierge)
 * A small search icon in the header (beside "The Technology") opens a right-side
 * slide-out drawer — white panel, concierge chat behaviour.
 *
 * Streamlined architecture — two zones, one vocabulary:
 *   transcript → real Q&A exchanges only (user bubble → typing dots → answer)
 *   navigator  → ONE persistent list under the transcript that swaps in place:
 *                home (popular + topics) → topic questions → related after an
 *                answer. Navigation is instant — no fake chat ceremony for it.
 *   search     → live suggestions anchored above the input (arrows + Enter)
 *
 * Content (6 categories × ~6 questions) is fetched + parsed from faq.html on
 * first open, so faq.html stays the single source of truth. Zero dependencies,
 * self-injecting, namespaced archer-faqd-* (no shared Webflow classes touched).
 */
(function () {
  'use strict';
  if (window.top !== window.self) return;

  // Dual-mode, mirrors archer-cart.js' window.Shopify detection so the porter ships
  // one file verbatim. On the live store, FETCH the ported AU FAQ page — it's the only
  // FAQ URL that keeps the mirror's .accordion_* markup the parser reads (36 Q&A) — while
  // the visible "View all FAQs" link points at the customer-facing /pages/faqs.
  // If /pages/au-faq is unpublished the fetch simply yields nothing and the drawer
  // falls back to the link (same as the pre-fix behaviour — no regression).
  var ON_SHOPIFY = !!window.Shopify;
  var FAQ_URL  = ON_SHOPIFY ? '/pages/au-faq' : '/archerskin.com/faq.html';
  var FAQ_HREF = ON_SHOPIFY ? '/pages/faqs'   : '/archerskin.com/faq.html';
  var TYPE_MS = 380;
  // Rotating example-question chips: how many show at once, and how long each
  // set rests before it ascends out and the next set ascends in.
  var PROMPT_N = 3;
  var PROMPT_MS = 3400;

  // Popular = these five, matched against parsed questions (fallback: category firsts).
  var POPULAR = [/how does archer actually work/i, /when will i see results/i,
    /safe for my eyes/i, /return policy/i, /pay in installments/i];

  // Built-in example questions. These render the instant the drawer opens, so the
  // navigator is never empty while the live FAQ fetch is in flight — and they REMAIN
  // if the fetch yields nothing (e.g. the ported /pages/au-faq is still unpublished),
  // so the drawer always shows real example questions instead of a bare link.
  // Answers mirror faq.html verbatim (cosmetic-only, "individual results vary" kept).
  var SEED = [
    { q: 'How does ARCHER actually work?', cat: 'Popular',
      a: '<p>ARCHER is a multi-wavelength LED device built on photobiomodulation — the principle behind professional light therapy, where specific wavelengths of light are absorbed by skin cells to support their natural processes. Each of its five wavelengths is tuned to a different depth. No UV, no abrasion, no uncomfortable heat — just one 10-minute session a day. Individual results vary.</p>' },
    { q: 'When will I see results?', cat: 'Popular',
      a: '<p>Many users notice an immediate glow after the very first 10-minute session. The look of texture, tone, and fine lines tends to develop gradually over several weeks of daily use, as with any wellness ritual. Individual results vary.</p>' },
    { q: 'How often and how long should I use it?', cat: 'Popular',
      a: '<p>One 10-minute session per day is the recommended ritual. The session is calibrated to deliver a complete dose in those ten minutes, so longer or more frequent use offers no added benefit.</p>' },
    { q: 'Is it safe for my eyes? Do I need goggles?', cat: 'Popular',
      a: '<p>The mask sits flush against the face, which keeps the light directed at your skin rather than your eyes — no goggles required. Most users simply close their eyes for comfort during the session. If you have a pre-existing eye condition, we recommend speaking with your physician before use.</p>' },
    { q: 'What is your return policy?', cat: 'Popular',
      a: '<p>We do not offer refunds, returns or exchanges for change of mind, so please choose carefully. If your device is faulty, you are covered by our 12-month warranty and by your non-excludable consumer rights — our concierge team will arrange a short video call to assess and resolve any fault.</p>' },
    { q: 'Can I pay in installments?', cat: 'Popular',
      a: '<p>Yes. Alongside standard cards, ARCHER offers installment options at checkout so you can split the price into smaller scheduled payments. Available plans and any provider terms are shown at checkout before you confirm.</p>' }
  ];

  var SEARCH_ICON =
    '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="1.6" stroke-linecap="round" aria-hidden="true">' +
    '<circle cx="10.5" cy="10.5" r="7"></circle><line x1="15.6" y1="15.6" x2="21" y2="21"></line></svg>';
  var SEND_ICON =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>';
  var CHEV_R =
    '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<polyline points="9 6 15 12 9 18"></polyline></svg>';
  var CHEV_L =
    '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<polyline points="15 6 9 12 15 18"></polyline></svg>';

  function ready(fn) {
    if (document.body) fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    injectStyles();
    var ui = buildDrawer();
    var trigger = injectTrigger(ui);
    if (!trigger) return;
    wire(trigger, ui);
  });

  /* ---------- styles (white panel, ARCHER ink accents) ---------- */
  function injectStyles() {
    var s = document.createElement('style');
    s.textContent = [
      '.archer-faqd-trigger{display:inline-flex;align-items:center;justify-content:center;background:none;',
        'border:0;padding:2px;margin:0;line-height:0;color:inherit;opacity:.82;transition:opacity .2s,color .25s;}',
      '.archer-faqd-trigger:hover{opacity:1;}',
      '.archer-nav-override .archer-faqd-trigger{color:#313437;}',
      '.archer-nav-override.over-dark .archer-faqd-trigger{color:#fff;}',
      '.archer-faqd-mlink{background:none;border:0;padding:0;font:inherit;color:inherit;cursor:pointer;}',
      '.archer-faqd-scrim{position:fixed;inset:0;z-index:100000;background:rgba(10,10,12,.44);',
        '-webkit-backdrop-filter:blur(2px);backdrop-filter:blur(2px);opacity:0;pointer-events:none;transition:opacity .34s ease;}',
      '.archer-faqd-scrim.is-open{opacity:1;pointer-events:auto;}',
      '.archer-faqd-panel{position:fixed;top:0;right:0;height:100%;width:min(400px,94vw);z-index:100001;',
        'background:#fff;color:#1b1b1b;display:flex;flex-direction:column;box-shadow:-18px 0 50px rgba(0,0,0,.22);',
        'transform:translateX(101%);transition:transform .40s cubic-bezier(.4,.01,.16,1);will-change:transform;font-family:inherit;}',
      '.archer-faqd-panel.is-open{transform:translateX(0);}',
      // header
      '.archer-faqd-head{display:flex;align-items:center;gap:12px;padding:20px 20px 18px;',
        'border-bottom:1px solid #ececec;flex:none;}',
      '.archer-faqd-avatar{width:34px;height:34px;border-radius:50%;background:#1b1b1b;color:#fff;font-size:14px;',
        'font-weight:600;display:flex;align-items:center;justify-content:center;flex:none;}',
      '.archer-faqd-htext{flex:1;min-width:0;}',
      '.archer-faqd-title{font-size:15px;font-weight:600;letter-spacing:.01em;margin:0;color:#1b1b1b;}',
      '.archer-faqd-sub{font-size:11.5px;color:#8a8a8c;margin:2px 0 0;}',
      '.archer-faqd-close{background:none;border:0;font-size:24px;line-height:1;color:#9a9a9a;',
        'cursor:pointer;padding:6px 8px;margin:-6px -8px -6px 0;transition:color .2s;flex:none;}',
      '.archer-faqd-close:hover{color:#1b1b1b;}',
      // window: transcript + navigator
      '.archer-faqd-win{flex:1;overflow-y:auto;overflow-x:hidden;padding:18px 16px;display:flex;flex-direction:column;gap:12px;',
        'overscroll-behavior:contain;-webkit-overflow-scrolling:touch;scroll-behavior:smooth;',
        'scrollbar-width:thin;scrollbar-color:rgba(0,0,0,.25) transparent;}',
      '.archer-faqd-win::-webkit-scrollbar{width:7px;}',
      '.archer-faqd-win::-webkit-scrollbar-thumb{background:rgba(0,0,0,.22);border-radius:4px;}',
      // messages
      '.archer-faqd-msg{display:flex;gap:10px;align-items:flex-end;max-width:88%;flex:none;}',
      '.archer-faqd-msg.ac-bot{align-self:flex-start;}',
      '.archer-faqd-msg.ac-user{align-self:flex-end;flex-direction:row-reverse;}',
      '.archer-faqd-mav{width:26px;height:26px;border-radius:50%;background:#1b1b1b;color:#fff;font-size:12px;',
        'font-weight:600;display:flex;align-items:center;justify-content:center;flex:none;}',
      '.archer-faqd-bubble{background:#f4f4f6;color:#1b1b1b;padding:11px 15px;border-radius:14px;',
        'font-size:14px;line-height:1.55;min-width:0;}',
      '.archer-faqd-bubble p{margin:0 0 9px;}.archer-faqd-bubble p:last-child{margin:0;}',
      '.archer-faqd-bubble a{color:#1b1b1b;text-decoration:underline;text-underline-offset:2px;}',
      '.archer-faqd-msg.ac-user .archer-faqd-bubble{background:#1b1b1b;color:#fff;}',
      // typing dots
      '.archer-faqd-msg.ac-typing .archer-faqd-bubble{display:flex;gap:5px;align-items:center;}',
      '.archer-faqd-msg.ac-typing .archer-faqd-bubble span{width:7px;height:7px;border-radius:50%;',
        'background:rgba(0,0,0,.4);animation:archer-faqd-blink 1.2s infinite;}',
      '.archer-faqd-msg.ac-typing .archer-faqd-bubble span:nth-child(2){animation-delay:.2s;}',
      '.archer-faqd-msg.ac-typing .archer-faqd-bubble span:nth-child(3){animation-delay:.4s;}',
      '@keyframes archer-faqd-blink{0%,60%,100%{opacity:.3}30%{opacity:1}}',
      // rotating example-question chips (ascend in, rest, ascend out, swap)
      '.archer-faqd-prompts{flex:none;margin:2px 0 4px;}',
      '.archer-faqd-prompts-lab{font-size:10.5px;font-weight:600;letter-spacing:.09em;text-transform:uppercase;',
        'color:#b2b2b4;padding:2px 4px 11px;}',
      '.archer-faqd-chips{display:flex;flex-direction:column;gap:8px;align-items:flex-start;}',
      '.archer-faqd-chips.is-out{animation:archer-faqd-riseout .38s ease forwards;}',
      '@keyframes archer-faqd-riseout{to{opacity:0;transform:translateY(-12px)}}',
      '@keyframes archer-faqd-risein{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}',
      '.archer-faqd-chip{display:inline-flex;align-items:center;max-width:100%;background:#f4f4f6;',
        'border:1px solid #ececee;border-radius:999px;padding:9px 15px;color:#2a2a2c;font-size:13px;',
        'line-height:1.35;font-family:inherit;text-align:left;cursor:pointer;',
        'animation:archer-faqd-risein .5s cubic-bezier(.4,.01,.16,1) both;',
        'transition:background .18s,border-color .18s,color .18s,transform .18s;}',
      '.archer-faqd-chip:hover{background:#1b1b1b;border-color:#1b1b1b;color:#fff;transform:translateX(2px);}',
      // navigator: one flat list, hairline dividers, no box chrome
      '.archer-faqd-nav{flex:none;margin-top:2px;animation:archer-faqd-fade .22s ease;}',
      '@keyframes archer-faqd-fade{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}',
      '.archer-faqd-sec{font-size:10.5px;font-weight:600;letter-spacing:.09em;text-transform:uppercase;',
        'color:#9a9a9c;padding:14px 4px 6px;}',
      '.archer-faqd-row{display:flex;align-items:center;justify-content:space-between;gap:10px;width:100%;',
        'background:none;border:0;border-bottom:1px solid rgba(0,0,0,.08);color:#2a2a2c;',
        'padding:12px 4px;font-size:13.5px;line-height:1.4;text-align:left;cursor:pointer;font-family:inherit;',
        'transition:color .15s;}',
      '.archer-faqd-row:hover{color:#000;}',
      '.archer-faqd-row:hover svg{color:rgba(0,0,0,.65);}',
      '.archer-faqd-row svg{flex:none;color:rgba(0,0,0,.3);transition:color .15s;}',
      '.archer-faqd-row.is-back{color:#8a8a8c;justify-content:flex-start;border-bottom:0;padding-bottom:4px;}',
      '.archer-faqd-row.is-back:hover{color:#1b1b1b;}',
      '.archer-faqd-alllink{text-decoration:none;color:#8a8a8c;border-bottom:0;margin-top:6px;}',
      '.archer-faqd-alllink:hover{color:#1b1b1b;}',
      // search suggestions (floating above the input)
      '.archer-faqd-bar-wrap{position:relative;flex:none;}',
      '.archer-faqd-sugg{position:absolute;left:12px;right:12px;bottom:100%;margin-bottom:8px;',
        'background:#fff;border:1px solid #e6e6e8;border-radius:14px;overflow:hidden;',
        'box-shadow:0 -8px 30px rgba(0,0,0,.14);display:none;}',
      '.archer-faqd-sugg.is-open{display:block;}',
      '.archer-faqd-sugg .archer-faqd-row{padding:11px 15px;border-bottom-color:rgba(0,0,0,.06);}',
      '.archer-faqd-sugg .archer-faqd-row:last-child{border-bottom:0;}',
      '.archer-faqd-sugg .archer-faqd-row.is-active{background:#f2f2f4;color:#000;}',
      '.archer-faqd-sugg-none{padding:12px 15px;font-size:12.5px;color:#8a8a8c;}',
      // ask bar
      '.archer-faqd-bar{display:flex;gap:9px;align-items:center;padding:14px 16px;',
        'border-top:1px solid #ececec;}',
      '.archer-faqd-field{flex:1;position:relative;display:flex;align-items:center;}',
      '.archer-faqd-field .archer-faqd-sico{position:absolute;left:15px;color:#9a9a9c;pointer-events:none;line-height:0;}',
      '.archer-faqd-input{width:100%;box-sizing:border-box;background:#f6f6f7;',
        'border:1px solid #e2e2e4;border-radius:999px;padding:12px 16px 12px 40px;color:#1b1b1b;',
        'font-size:14px;font-family:inherit;outline:none;transition:border-color .2s,background .2s;}',
      '.archer-faqd-input::placeholder{color:#9a9a9c;}',
      '.archer-faqd-input:focus{border-color:#bcbcbe;background:#fff;}',
      '.archer-faqd-send{width:44px;height:44px;border-radius:50%;background:#1b1b1b;color:#fff;border:0;cursor:pointer;',
        'display:flex;align-items:center;justify-content:center;flex:none;transition:transform .2s;}',
      '.archer-faqd-send:hover{transform:scale(1.06);}',
      '.archer-faqd-send svg{width:18px;height:18px;}',
      'html.archer-faqd-lock,html.archer-faqd-lock body{overflow:hidden;}'
    ].join('');
    document.head.appendChild(s);
  }

  /* ---------- trigger (desktop nav + mobile menu) ---------- */
  function injectTrigger(ui) {
    var header = document.querySelector('.archer-nav-override');
    if (!header) return null;

    var techLink = null;
    header.querySelectorAll('nav a.archer-nav-secondary').forEach(function (a) {
      if (/tech/i.test(a.textContent)) techLink = a;
    });
    var navs = header.querySelectorAll('nav');
    var container = techLink ? techLink.parentNode : (navs.length ? navs[navs.length - 1] : header);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'archer-faqd-trigger';
    btn.setAttribute('aria-label', 'Ask ARCHER — search FAQs');
    btn.setAttribute('aria-haspopup', 'dialog');
    btn.innerHTML = SEARCH_ICON;
    if (techLink) container.insertBefore(btn, techLink);
    else container.appendChild(btn);

    /* document-scoped: the nav drawer is re-parented to <body> by the page's
       drawer script (frosted-header containing-block fix) */
    var mLinks = document.querySelector('.archer-nav-drawer__links');
    if (mLinks) {
      var mBtn = document.createElement('button');
      mBtn.type = 'button';
      mBtn.className = 'archer-faqd-mlink';
      mBtn.textContent = 'Ask ARCHER';
      mBtn.addEventListener('click', function () {
        var burger = header.querySelector('.archer-nav-burger');
        if (burger && document.querySelector('.archer-nav-drawer.is-open')) burger.click();
        open(ui);
      });
      /* sits under "The Technology": insert before the Account link */
      var mAccount = mLinks.querySelector('[data-nav-account]');
      if (mAccount) mLinks.insertBefore(mBtn, mAccount);
      else mLinks.appendChild(mBtn);
    }
    return btn;
  }

  /* ---------- drawer DOM ---------- */
  function buildDrawer() {
    var scrim = document.createElement('div');
    scrim.className = 'archer-faqd-scrim';

    var panel = document.createElement('aside');
    panel.className = 'archer-faqd-panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-label', 'Ask ARCHER');
    panel.setAttribute('aria-hidden', 'true');
    panel.innerHTML =
      '<div class="archer-faqd-head">' +
        '<span class="archer-faqd-avatar">A</span>' +
        '<div class="archer-faqd-htext"><h2 class="archer-faqd-title">Ask ARCHER</h2>' +
        '<p class="archer-faqd-sub">Instant answers, any page</p></div>' +
        '<button class="archer-faqd-close" type="button" aria-label="Close">×</button>' +
      '</div>' +
      '<div class="archer-faqd-win" data-lenis-prevent>' +
        '<div class="archer-faqd-nav"></div>' +
      '</div>' +
      '<div class="archer-faqd-bar-wrap">' +
        '<div class="archer-faqd-sugg" role="listbox"></div>' +
        '<div class="archer-faqd-bar">' +
          '<div class="archer-faqd-field"><span class="archer-faqd-sico">' + SEARCH_ICON + '</span>' +
          '<input class="archer-faqd-input" type="search" placeholder="Ask or search questions…" ' +
          'aria-label="Ask or search a question" autocomplete="off"></div>' +
          '<button class="archer-faqd-send" type="button" aria-label="Ask">' + SEND_ICON + '</button>' +
        '</div>' +
      '</div>';

    document.body.appendChild(scrim);
    document.body.appendChild(panel);
    return {
      scrim: scrim, panel: panel,
      win: panel.querySelector('.archer-faqd-win'),
      nav: panel.querySelector('.archer-faqd-nav'),
      sugg: panel.querySelector('.archer-faqd-sugg'),
      input: panel.querySelector('.archer-faqd-input'),
      send: panel.querySelector('.archer-faqd-send'),
      close: panel.querySelector('.archer-faqd-close'),
      cats: [], all: [], suggIdx: -1,
      loaded: false, busy: false, lastFocus: null
    };
  }

  /* ---------- open / close ---------- */
  function open(ui) {
    ui.lastFocus = document.activeElement;
    ui.scrim.classList.add('is-open');
    ui.panel.classList.add('is-open');
    ui.panel.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('archer-faqd-lock');
    if (!ui.loaded) {
      ui.loaded = true;
      // Show the greeting + example questions instantly — the live fetch enriches after.
      botBubble(ui, 'How can I help?');
      SEED.forEach(function (it) { ui.all.push(it); });
      loadFaqs(ui);
    }
    startPrompts(ui);   // chips first, so navHome knows to skip its Popular section
    navHome(ui, false);
    setTimeout(function () { ui.input.focus(); }, 400);
  }
  function close(ui) {
    stopPrompts(ui);
    hideSugg(ui);
    ui.scrim.classList.remove('is-open');
    ui.panel.classList.remove('is-open');
    ui.panel.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('archer-faqd-lock');
    if (ui.lastFocus && ui.lastFocus.focus) ui.lastFocus.focus();
  }

  function wire(trigger, ui) {
    trigger.addEventListener('click', function () { open(ui); });
    ui.close.addEventListener('click', function () { close(ui); });
    ui.scrim.addEventListener('click', function () { close(ui); });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !ui.panel.classList.contains('is-open')) return;
      if (ui.sugg.classList.contains('is-open')) hideSugg(ui);
      else close(ui);
    });

    // Navigator + suggestions share one row vocabulary.
    function onRowClick(e) {
      var q = e.target.closest('[data-q]');
      if (q) { clearInput(ui); askQuestion(ui, q.getAttribute('data-q')); return; }
      var t = e.target.closest('[data-topic]');
      if (t) { navTopic(ui, t.getAttribute('data-topic')); return; }
      if (e.target.closest('[data-home]')) navHome(ui, true);
    }
    ui.win.addEventListener('click', onRowClick);
    ui.sugg.addEventListener('click', onRowClick);

    ui.input.addEventListener('input', function () { renderSugg(ui, this.value); });
    ui.input.addEventListener('keydown', function (e) {
      var rows = ui.sugg.querySelectorAll('.archer-faqd-row');
      if (e.key === 'ArrowDown' && rows.length) { e.preventDefault(); moveSugg(ui, 1); }
      else if (e.key === 'ArrowUp' && rows.length) { e.preventDefault(); moveSugg(ui, -1); }
      else if (e.key === 'Enter') { e.preventDefault(); submitInput(ui); }
    });
    ui.send.addEventListener('click', function () { submitInput(ui); });
  }

  /* ---------- content: fetch + parse faq.html, enriching the seed ---------- */
  function loadFaqs(ui) {
    fetch(FAQ_URL, { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var all = [], cats = [];
        doc.querySelectorAll('.faq_category').forEach(function (block) {
          var name = 'Questions';
          var els = block.querySelectorAll('[class*="heading"],[class*="label"]');
          for (var i = 0; i < els.length; i++) {
            if (!els[i].closest('.accordion_item')) { name = (els[i].textContent || '').trim(); break; }
          }
          var items = [];
          block.querySelectorAll('.accordion_item').forEach(function (it) {
            var qEl = it.querySelector('.accordion_toggle_text');
            var aEl = it.querySelector('.accordion_content_text');
            var q = qEl ? (qEl.textContent || '').trim() : '';
            if (!q) return;
            var entry = { q: q, a: aEl ? aEl.innerHTML : '', cat: name };
            items.push(entry); all.push(entry);
          });
          if (items.length) cats.push({ name: name, items: items });
        });
        // Only swap in the fetched content if the parse actually found questions —
        // otherwise the built-in seed stays put (no regression to a bare link).
        if (all.length) { ui.all = all; ui.cats = cats; navHome(ui, false); }
      })
      .catch(function () { /* keep the seed already on screen */ });
  }

  /* ---------- navigator (swaps in place — navigation is instant) ---------- */
  function navSwap(ui, html) {
    ui.nav.innerHTML = html;
    // retrigger the entrance fade
    ui.nav.style.animation = 'none';
    void ui.nav.offsetWidth;
    ui.nav.style.animation = '';
  }
  function rowHTML(it) {
    return '<button type="button" class="archer-faqd-row" data-q="' + escapeHTML(it.q) + '">' +
      '<span>' + escapeHTML(it.q) + '</span>' + CHEV_R + '</button>';
  }
  function backRowHTML(label) {
    return '<button type="button" class="archer-faqd-row is-back" data-home="1">' + CHEV_L +
      '<span>' + escapeHTML(label || 'All topics') + '</span></button>';
  }

  function navHome(ui, scroll) {
    // Seed mode (live fetch hasn't populated categories): show the example
    // questions and a link to the full FAQ, skip the empty "Browse by topic".
    if (!ui.cats.length) {
      var hs = '';
      // The rotating "Try asking" chips already surface these — only fall back to a
      // static list when the chips aren't on screen (e.g. after an answer).
      if (!ui.promptWrap) {
        hs += '<div class="archer-faqd-sec">Popular questions</div>';
        ui.all.forEach(function (it) { hs += rowHTML(it); });
      }
      hs += '<a class="archer-faqd-row archer-faqd-alllink" href="' + FAQ_HREF + '">' +
        '<span>Browse all FAQs</span>' + CHEV_R + '</a>';
      navSwap(ui, hs);
      if (scroll) scrollToEl(ui, ui.nav);
      return;
    }

    var h = '';
    // With the rotating chips on screen, skip the static Popular list to avoid
    // duplicating them; the chips are the animated equivalent.
    if (!ui.promptWrap) {
      var pop = [];
      POPULAR.forEach(function (re) {
        var hit = ui.all.filter(function (it) { return re.test(it.q); })[0];
        if (hit && pop.indexOf(hit) < 0) pop.push(hit);
      });
      ui.cats.forEach(function (c) { if (pop.length < 5 && pop.indexOf(c.items[0]) < 0) pop.push(c.items[0]); });
      h += '<div class="archer-faqd-sec">Popular questions</div>';
      pop.slice(0, 5).forEach(function (it) { h += rowHTML(it); });
    }
    h += '<div class="archer-faqd-sec">Browse by topic</div>';
    ui.cats.forEach(function (c) {
      h += '<button type="button" class="archer-faqd-row" data-topic="' + escapeHTML(c.name) + '">' +
        '<span>' + escapeHTML(c.name) + '</span>' + CHEV_R + '</button>';
    });
    navSwap(ui, h);
    if (scroll) scrollToEl(ui, ui.nav);
  }

  function navTopic(ui, name) {
    var cat = null;
    ui.cats.forEach(function (c) { if (c.name === name) cat = c; });
    if (!cat) return;
    var h = backRowHTML() + '<div class="archer-faqd-sec">' + escapeHTML(cat.name) + '</div>';
    cat.items.forEach(function (it) { h += rowHTML(it); });
    navSwap(ui, h);
    scrollToEl(ui, ui.nav);
  }

  function navRelated(ui, item) {
    var rel = [];
    ui.cats.forEach(function (c) {
      if (c.name !== item.cat) return;
      c.items.forEach(function (it) { if (it !== item && rel.length < 3) rel.push(it); });
    });
    var h = '';
    if (rel.length) {
      h += '<div class="archer-faqd-sec">Related</div>';
      rel.forEach(function (it) { h += rowHTML(it); });
    }
    h += backRowHTML();
    navSwap(ui, h);
  }

  /* ---------- rotating example-question chips ---------- */
  // A compact "Try asking" strip above the navigator. Shows PROMPT_N clickable
  // questions that ascend in, rest, then ascend out while the next set rotates
  // in — cycling through the whole question pool so the drawer never feels idle.
  // Clicking a chip routes through the shared data-q handler (askQuestion), which
  // stops the rotation once a real conversation begins.
  function startPrompts(ui) {
    stopPrompts(ui);
    if (!ui.all.length) return;
    var wrap = document.createElement('div');
    wrap.className = 'archer-faqd-prompts';
    wrap.innerHTML = '<div class="archer-faqd-prompts-lab">Try asking</div>' +
      '<div class="archer-faqd-chips"></div>';
    ui.win.insertBefore(wrap, ui.nav);
    ui.promptWrap = wrap;
    ui.promptChips = wrap.querySelector('.archer-faqd-chips');
    ui.promptIdx = 0;
    cyclePrompts(ui, true);
    ui.promptTimer = setInterval(function () { cyclePrompts(ui, false); }, PROMPT_MS);
  }
  function stopPrompts(ui) {
    if (ui.promptTimer) { clearInterval(ui.promptTimer); ui.promptTimer = null; }
    if (ui.promptWrap) ui.promptWrap.remove();
    ui.promptWrap = null; ui.promptChips = null;
  }
  function renderPromptChips(ui) {
    var pool = ui.all, h = '';
    for (var i = 0; i < PROMPT_N; i++) {
      var it = pool[ui.promptIdx % pool.length];
      ui.promptIdx++;
      h += '<button type="button" class="archer-faqd-chip" data-q="' + escapeHTML(it.q) + '" ' +
        'style="animation-delay:' + (i * 90) + 'ms"><span>' + escapeHTML(it.q) + '</span></button>';
    }
    return h;
  }
  function cyclePrompts(ui, first) {
    var chips = ui.promptChips;
    if (!chips) return;
    if (first) { chips.innerHTML = renderPromptChips(ui); return; }
    chips.classList.add('is-out');
    setTimeout(function () {
      if (!ui.promptChips) return;
      chips.classList.remove('is-out');
      chips.innerHTML = renderPromptChips(ui);
    }, 380); // match archer-faqd-riseout duration
  }

  /* ---------- conversation (transcript is for real Q&A only) ---------- */
  function askQuestion(ui, q) {
    if (ui.busy) return;
    stopPrompts(ui);
    var item = null;
    ui.all.forEach(function (it) { if (it.q === q) item = it; });
    ui.busy = true;
    var um = userBubble(ui, q);
    scrollToEl(ui, um);
    var t = typing(ui);
    setTimeout(function () {
      t.remove();
      if (item) {
        botBubble(ui, item.a || 'See the full answer on our <a href="' + FAQ_HREF + '">FAQ page →</a>', true);
        navRelated(ui, item);
      } else {
        botBubble(ui, 'I couldn’t find that one. Reach us anytime at ' +
          '<a href="mailto:concierge@archerskin.com">concierge@archerskin.com</a>, or ' +
          '<a href="' + FAQ_HREF + '">browse all FAQs →</a>', true);
        navHome(ui, false);
      }
      scrollToEl(ui, um);
      ui.busy = false;
    }, TYPE_MS);
  }

  function botBubble(ui, html, rich) {
    var m = document.createElement('div');
    m.className = 'archer-faqd-msg ac-bot';
    m.innerHTML = '<span class="archer-faqd-mav">A</span><div class="archer-faqd-bubble">' +
      (rich ? html : escapeHTML(html)) + '</div>';
    ui.win.insertBefore(m, ui.nav);
    return m;
  }
  function userBubble(ui, text) {
    var m = document.createElement('div');
    m.className = 'archer-faqd-msg ac-user';
    m.innerHTML = '<div class="archer-faqd-bubble">' + escapeHTML(text) + '</div>';
    ui.win.insertBefore(m, ui.nav);
    return m;
  }
  function typing(ui) {
    var m = document.createElement('div');
    m.className = 'archer-faqd-msg ac-bot ac-typing';
    m.innerHTML = '<span class="archer-faqd-mav">A</span><div class="archer-faqd-bubble">' +
      '<span></span><span></span><span></span></div>';
    ui.win.insertBefore(m, ui.nav);
    return m;
  }
  function scrollToEl(ui, el) {
    if (!el || !el.getBoundingClientRect) return;
    var delta = el.getBoundingClientRect().top - ui.win.getBoundingClientRect().top;
    ui.win.scrollTop += delta - 14;
  }

  /* ---------- search suggestions ---------- */
  function score(it, term) {
    var q = it.q.toLowerCase();
    if (q.indexOf(term) === 0) return 3;
    if (q.indexOf(' ' + term) > -1) return 2;
    if (q.indexOf(term) > -1) return 1;
    var a = (it._plain = it._plain || stripTags(it.a).toLowerCase());
    return a.indexOf(term) > -1 ? 0.5 : 0;
  }
  function matches(ui, term) {
    term = term.trim().toLowerCase();
    if (!term) return [];
    return ui.all
      .map(function (it) { return { it: it, s: score(it, term) }; })
      .filter(function (x) { return x.s > 0; })
      .sort(function (a, b) { return b.s - a.s; })
      .slice(0, 5)
      .map(function (x) { return x.it; });
  }
  function renderSugg(ui, term) {
    var hits = matches(ui, term);
    ui.suggIdx = -1;
    if (!term.trim() || !ui.all.length) return hideSugg(ui);
    var h = '';
    hits.forEach(function (it) {
      h += '<button type="button" class="archer-faqd-row" role="option" data-q="' + escapeHTML(it.q) + '">' +
        '<span>' + escapeHTML(it.q) + '</span>' + CHEV_R + '</button>';
    });
    if (!hits.length) h = '<div class="archer-faqd-sugg-none">No matches — press ↵ to ask the concierge.</div>';
    ui.sugg.innerHTML = h;
    ui.sugg.classList.add('is-open');
  }
  function hideSugg(ui) { ui.sugg.classList.remove('is-open'); ui.sugg.innerHTML = ''; ui.suggIdx = -1; }
  function moveSugg(ui, dir) {
    var rows = ui.sugg.querySelectorAll('.archer-faqd-row');
    if (!rows.length) return;
    ui.suggIdx = (ui.suggIdx + dir + rows.length) % rows.length;
    rows.forEach(function (r, i) { r.classList.toggle('is-active', i === ui.suggIdx); });
  }
  function clearInput(ui) { ui.input.value = ''; hideSugg(ui); }

  function submitInput(ui) {
    var term = ui.input.value.trim();
    if (!term || ui.busy) return;
    var rows = ui.sugg.querySelectorAll('.archer-faqd-row');
    var pick = rows.length ? rows[ui.suggIdx >= 0 ? ui.suggIdx : 0].getAttribute('data-q') : null;
    clearInput(ui);
    if (pick) return askQuestion(ui, pick);
    // no match — concierge fallback, echoing what they typed
    var um = userBubble(ui, term);
    scrollToEl(ui, um);
    var t = typing(ui);
    setTimeout(function () {
      t.remove();
      botBubble(ui, 'I couldn’t find that one. Reach us anytime at ' +
        '<a href="mailto:concierge@archerskin.com">concierge@archerskin.com</a>, or ' +
        '<a href="' + FAQ_HREF + '">browse all FAQs →</a>', true);
      scrollToEl(ui, um);
    }, TYPE_MS);
  }

  function stripTags(html) {
    var d = document.createElement('div');
    d.innerHTML = html;
    return d.textContent || '';
  }
  function escapeHTML(s) {
    return String(s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }
})();
